import { z } from 'zod';

// S01 filters live in the URL (07 S01): the route's `validateSearch` is this
// schema. Keys are the list endpoint's own query keys wherever the API has
// the filter, so a saved view's `filters` is the URL's search as it is.

// The router parses search values as JSON (`?q=4402` is a number, `?attention=true` a boolean).
const asString = (v: unknown) => (typeof v === 'number' ? String(v) : v);
const text = z.preprocess(asString, z.string().trim().min(1).max(64)).optional().catch(undefined);
const csv = z
  .string()
  .regex(/^[\w-]+(,[\w-]+)*$/)
  .max(256)
  .optional()
  .catch(undefined);
const flag = z
  .preprocess((v) => (v === 'true' ? true : v), z.literal(true))
  .optional()
  .catch(undefined);
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

export const listSearchSchema = z.object({
  /** Reference, container, B/L, booking or Dokless id. */
  q: text,
  status: csv,
  carrier: csv,
  /** Alert code(s) («Segnale»). */
  alert: csv,
  cargo: csv,
  /** Read mode: auto / recipe / model. */
  mode: csv,
  /** «Solo da guardare». */
  attention: flag,
  /** «Controllo scaduto» → `due=overdue`. */
  due: z.enum(['overdue', 'today', '48h']).optional().catch(undefined),
  /** «In arrivo < 48 h» tile. */
  arriving: z.literal('48h').optional().catch(undefined),
  /** «Senza posizione > 12 h». */
  no_position: z.literal('12h').optional().catch(undefined),
  /** ETD / ETA window (inclusive days, local). */
  etd_from: day,
  etd_to: day,
  eta_from: day,
  eta_to: day,
  /** `field` or `-field` (the API's sorts). */
  sort: z
    .string()
    .regex(/^-?(updated_at|created_at|next_check_at|eta|last_fact_at|attention_since|reference|status)$/)
    .optional()
    .catch(undefined),
  /** The saved view the filters came from (built-in id or a saved view's id). */
  view: z.string().max(64).optional().catch(undefined),
});
export type ListSearch = z.infer<typeof listSearchSchema>;

/** The keys that filter (everything but sort and view). */
export const FILTER_KEYS = [
  'q',
  'status',
  'carrier',
  'alert',
  'cargo',
  'mode',
  'attention',
  'due',
  'arriving',
  'no_position',
  'etd_from',
  'etd_to',
  'eta_from',
  'eta_to',
] as const satisfies readonly (keyof ListSearch)[];
export type FilterKey = (typeof FILTER_KEYS)[number];

export function hasFilters(search: ListSearch): boolean {
  return FILTER_KEYS.some((k) => search[k] !== undefined);
}

/** The filters only, as a saved view stores them. */
export function filtersOf(search: ListSearch): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of FILTER_KEYS) {
    const v = search[k];
    if (v !== undefined) out[k] = String(v);
  }
  return out;
}

/** Keys the API's list endpoint filters by (all of the filter bar's since the P5 close-out). */
const API_KEYS = [
  'q',
  'status',
  'carrier',
  'alert',
  'cargo',
  'mode',
  'attention',
  'due',
  'arriving',
  'no_position',
  'etd_from',
  'etd_to',
  'eta_from',
  'eta_to',
] as const;

/** The list endpoint's query for a URL search (`limit` and `cursor` added by the caller). */
export function toApiParams(search: ListSearch): URLSearchParams {
  const params = new URLSearchParams();
  for (const k of API_KEYS) {
    const v = search[k];
    if (v !== undefined) params.set(k, String(v));
  }
  if (search.sort) params.set('sort', search.sort);
  return params;
}

export const splitCsv = (v: string | undefined): string[] => (v ? v.split(',').filter(Boolean) : []);
export const joinCsv = (values: readonly string[]): string | undefined =>
  values.length ? [...new Set(values)].join(',') : undefined;

/** Toggle one value in a csv filter. */
export function toggleCsv(current: string | undefined, value: string): string | undefined {
  const list = splitCsv(current);
  return joinCsv(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
}

// ── counter tiles (07 S01: clickable = filter) ──────────────────────────

export const TILES = [
  'attention',
  'pre_departure',
  'in_transit',
  'arriving',
  'at_transshipment',
  'arrived',
] as const;
export type Tile = (typeof TILES)[number];

const TILE_FILTER: Record<Tile, Partial<ListSearch>> = {
  attention: { attention: true },
  pre_departure: { status: 'pre_departure' },
  in_transit: { status: 'in_transit' },
  arriving: { arriving: '48h' },
  at_transshipment: { status: 'at_transshipment' },
  arrived: { status: 'arrived' },
};

export function tileOn(search: ListSearch, tile: Tile): boolean {
  return Object.entries(TILE_FILTER[tile]).every(([k, v]) => search[k as keyof ListSearch] === v);
}

/** Clicking a tile toggles its filter, replacing the other tiles' filters. */
export function pickTile(search: ListSearch, tile: Tile): ListSearch {
  const on = tileOn(search, tile);
  const next: ListSearch = { ...search, view: undefined };
  for (const t of TILES) for (const k of Object.keys(TILE_FILTER[t])) delete next[k as keyof ListSearch];
  return on ? next : { ...next, ...TILE_FILTER[tile] };
}

// ── saved views (07 S01: «I miei di oggi», «Bloccate da dati Dokless», «Problemi sito») ──

export const BUILTIN_VIEWS = [
  { id: 'today', filters: { due: 'today' } },
  { id: 'dokless-data', filters: { status: 'blocked' } },
  { id: 'site-problems', filters: { alert: 'site_down,anti_bot,captcha,recipe_broken' } },
] as const satisfies readonly { id: string; filters: Partial<ListSearch> }[];
export type BuiltinViewId = (typeof BUILTIN_VIEWS)[number]['id'];

/** A search made of a view's filters only (keeps the sort). */
export function applyView(
  search: ListSearch,
  view: { id: string; filters: Record<string, string>; sort?: string | null } | null,
): ListSearch {
  if (!view) return { sort: search.sort };
  const parsed = listSearchSchema.parse({ ...view.filters, sort: view.sort ?? search.sort });
  return { ...parsed, view: view.id };
}

/** The search without any filter (the «Cancella filtri» button). */
export function clearFilters(search: ListSearch): ListSearch {
  return { sort: search.sort };
}

/** The sort after clicking a column: descending first, then ascending, then the default. */
export function nextSort(current: string | undefined, field: string): string | undefined {
  if (current === `-${field}`) return field;
  if (current === field) return undefined;
  return `-${field}`;
}
